import { useCallback, useState } from 'react'
import {
  adicionarPresetDoInstrutor,
  cenaDoPresetDoInstrutor,
  criarPresetDoInstrutor,
  desserializarPresetsDoInstrutor,
  removerPresetDoInstrutor,
  serializarPresetsDoInstrutor,
  type CenaTabletop,
  type PresetDoInstrutor,
} from '../../dominio/tabletop'

// a chave guarda o nome antigo do projeto (BIZU-LAB): trocá-la perderia os presets já salvos
export const CHAVE_PRESETS_DO_INSTRUTOR = 'bizu-lab-presets-do-instrutor-v1'

function ler(): PresetDoInstrutor[] {
  try {
    return desserializarPresetsDoInstrutor(window.localStorage.getItem(CHAVE_PRESETS_DO_INSTRUTOR))
  } catch {
    return []
  }
}

export interface ResultadoPreset {
  readonly sucesso: boolean
  readonly mensagem: string
}

/**
 * As montagens que o instrutor salvou como preset, guardadas neste navegador
 * (localStorage). Sem armazenamento (janela privada, cota cheia) o app segue
 * funcionando e avisa que o preset não ficou guardado; o Exportar JSON
 * continua sendo o jeito de levar a montagem para outro computador.
 */
export function usePresetsDoInstrutor() {
  const [presets, setPresets] = useState<PresetDoInstrutor[]>(ler)

  const gravar = useCallback((lista: PresetDoInstrutor[]): boolean => {
    setPresets(lista)
    try {
      window.localStorage.setItem(CHAVE_PRESETS_DO_INSTRUTOR, serializarPresetsDoInstrutor(lista))
      return true
    } catch {
      return false
    }
  }, [])

  const salvar = useCallback(
    (cena: CenaTabletop, nome: string): ResultadoPreset => {
      let novo: PresetDoInstrutor
      try {
        novo = criarPresetDoInstrutor(cena, nome, Date.now())
      } catch {
        return { sucesso: false, mensagem: 'A cena tem algo inválido e não pôde ser salva como preset.' }
      }
      const guardou = gravar(adicionarPresetDoInstrutor(ler(), novo))
      return guardou
        ? { sucesso: true, mensagem: `Preset "${novo.nome}" salvo neste navegador.` }
        : { sucesso: false, mensagem: 'Este navegador não deixou guardar o preset. Use Exportar JSON para não perder a montagem.' }
    },
    [gravar],
  )

  const excluir = useCallback((id: string) => gravar(removerPresetDoInstrutor(ler(), id)), [gravar])

  /** A cena do preset, ou `null` se o conteúdo guardado deixou de ser válido. */
  const abrir = useCallback(
    (id: string): CenaTabletop | null => {
      const preset = presets.find((p) => p.id === id)
      if (!preset) return null
      try {
        return cenaDoPresetDoInstrutor(preset)
      } catch {
        return null
      }
    },
    [presets],
  )

  return { presets, salvar, excluir, abrir }
}
